import { useTranslator } from '@translator-app/react';
import classNames from 'classnames';
import Fuse from 'fuse.js';
import { GetServerSidePropsContext } from 'next';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import React, { useMemo, useState } from 'react';

import { Button, LinkButton } from '../../../components/atoms/button';
import { Heading } from '../../../components/atoms/heading';
import { Image } from '../../../components/atoms/image';
import { AppBanner } from '../../../components/organisms/app-banner';
import { ErrorFallback } from '../../../components/organisms/error-boundary';
import { NotFound } from '../../../components/organisms/not-found';
import { OrderHint } from '../../../components/organisms/order-hint';
import { FallbackPageTemplate } from '../../../components/templates/fallback-page-template';
import { ShopPageTemplate } from '../../../components/templates/shop-page-template';
import { ShoppingCart, useApplicationState } from '../../../contexts/application-state-context';
import { KIOSK_TOGGLE_SECRET, useKioskMode } from '../../../contexts/kiosk-context';
import { useLanguageContext } from '../../../contexts/language-context';
import {
  AllergensEnumType,
  OrderObjectType,
  OrganisationObjectType,
  ReservationObjectType,
  ReservationStatusEnumType,
  ShopObjectType,
} from '../../../generated/graphql';
import SearchIcon from '../../../icons/search.svg';
import { MinimalServerError } from '../../../types';
import { getOrganisationAndShop } from '../../../utils/api';
import { formatCurrency } from '../../../utils/currency';
import { ImageFitMode } from '../../../utils/image';
import { nullthrows } from '../../../utils/invariant';
import { formatAmount } from '../../../utils/number';
import {
  getLocalisedString,
  getReservationPrices,
  getReservationProductImages,
  getReservationProductsName,
} from '../../../utils/product';
import {
  ReservationGroup,
  getAvailableReservationsInGroup,
  groupReservations,
  isReservationAvailable,
} from '../../../utils/reservation-grouping';
import { Sentry, captureException } from '../../_app';

const CAROUSEL_TIMEOUT = 5000;

export interface ProductCardProps {
  reservationGroup: ReservationGroup;
  organisation: OrganisationObjectType;
}

function ProductCard(props: ProductCardProps) {
  let { reservationGroup, organisation } = props;
  let router = useRouter();
  let { addToCart, shopId, shoppingCart } = useApplicationState();
  const reservation = reservationGroup.reservations[0];
  const availableReservations = React.useMemo(() => {
    const cartIds = new Set(shoppingCart.map((item) => item._id));
    return getAvailableReservationsInGroup(reservationGroup).filter((reservation) => !cartIds.has(reservation._id));
  }, [reservationGroup, shoppingCart]);
  const [orderQuantity, setOrderQuantity] = React.useState(1);
  let [shownImage, setShownImage] = React.useState(0);
  let [images, setImages] = React.useState<string[]>([]);
  let [carouselTimeout, setCarouselTimeout] = React.useState<number | null>(null);
  let { language } = useLanguageContext();
  let { translate } = useTranslator();

  const reservationPrices = React.useMemo(() => {
    return getReservationPrices(reservation);
  }, [reservation]);

  let name = React.useMemo(() => {
    return getReservationProductsName(reservation, language) || 'Removed product';
  }, [reservation.vendingProducts]);

  let discountPercentage = React.useMemo(() => {
    return reservation?.vendingDiscount ? formatAmount(reservation.vendingDiscount / 100) : '';
  }, [reservation]);

  const allergens = React.useMemo(() => {
    const allergensSet = new Set<AllergensEnumType>();
    for (const product of reservation.vendingProducts) {
      if (product.product?.allergensCombined) {
        for (const allergen of product.product.allergensCombined) {
          if (allergen && allergen !== AllergensEnumType.None) allergensSet.add(allergen);
        }
      }
      for (const included of product.product?.includedProducts ?? []) {
        if (included.product?.allergensCombined) {
          for (const allergen of included.product.allergensCombined) {
            if (allergen && allergen !== AllergensEnumType.None) allergensSet.add(allergen);
          }
        }
      }
    }
    for (const product of reservation.freeProducts) {
      if (product.product?.allergensCombined) {
        for (const allergen of product.product.allergensCombined) {
          if (allergen && allergen !== AllergensEnumType.None) allergensSet.add(allergen);
        }
      }
    }
    return Array.from(allergensSet);
  }, [reservation]);

  React.useEffect(() => {
    setImages(getReservationProductImages(reservation));
    setShownImage(0);
  }, [reservation.vendingProducts]);

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      if (carouselTimeout) {
        clearTimeout(carouselTimeout);
      }

      let timeout = setTimeout(() => {
        if (shownImage < images.length - 1) {
          setShownImage(shownImage + 1);
        } else {
          setShownImage(0);
        }
      }, CAROUSEL_TIMEOUT);

      setCarouselTimeout(timeout as unknown as number);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [images, shownImage, typeof window]);

  const isAvailable = isReservationAvailable(reservation);
  const isSold = !isAvailable;
  const vendingDiscount = reservation.vendingDiscount ?? 0;
  const availableCount = availableReservations.length;

  React.useEffect(() => {
    setOrderQuantity((current) => Math.min(current, Math.max(availableCount, 1)));
  }, [availableCount]);

  const hasImage = images.length > 0;

  const discountBadge = !!discountPercentage && !isSold && (
    <span className="inline-flex items-center rounded-full bg-red-600 px-4 py-1.5 text-base font-bold text-white shadow-sm">
      {translate('product.discountLabel', { percentage: discountPercentage })}
    </span>
  );

  const soldBadge = isSold && (
    <span className="inline-flex items-center rounded-full bg-primary px-5 py-2 text-lg font-bold text-white shadow-lg">
      {translate('product.sold')}
    </span>
  );

  return (
    <div className="group flex h-full transform flex-col overflow-hidden rounded-2xl border border-border bg-white transition hover:-translate-y-1 hover:border-primary hover:shadow-xl">
      {hasImage && (
        <div className="relative w-full overflow-hidden bg-cream" style={{ paddingBottom: '75%' }}>
          <NextLink
            href={`/${organisation.pathname}/${shopId}/product/${reservation._id}`}
            className="absolute inset-0 block"
          >
            <Image
              url={images[shownImage]}
              width={500}
              height={375}
              fitMode={ImageFitMode.Crop}
              sizes="(max-width: 500px) 400px, 800px"
              alt={name}
              className={classNames('h-full w-full object-cover transition transform', {
                grayscale: isSold,
                'group-hover:scale-105': !isSold,
              })}
            />
          </NextLink>

          {discountBadge && <div className="absolute left-3 top-3">{discountBadge}</div>}
          {soldBadge && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/10">
              {soldBadge}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-1 flex-col p-5">
        {!hasImage && (discountBadge || soldBadge) && (
          <div className="mb-3 flex flex-wrap items-center gap-2">
            {discountBadge}
            {soldBadge}
          </div>
        )}

        <NextLink href={`/${organisation.pathname}/${shopId}/product/${reservation._id}`}>
          <h3 className="font-display-bold text-lg leading-tight text-dark group-hover:text-primary">{name}</h3>
        </NextLink>

        {allergens.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {allergens
              .filter((a) => a !== AllergensEnumType.None)
              .map((allergen) => {
                const label = translate(`allergenEnum.${allergen}`);
                return (
                  <img
                    key={allergen}
                    src={`/allergen-icons/${allergen}.svg`}
                    alt={label}
                    title={label}
                    className="h-6 w-6 object-contain"
                  />
                );
              })}
          </div>
        )}

        <div className="mt-3 flex items-baseline gap-2">
          {vendingDiscount > 0 && (
            <span className="text-sm text-muted-foreground line-through">
              {formatCurrency(reservationPrices.totalPrice)}
            </span>
          )}
          <span
            className={classNames('font-display-bold', {
              'text-3xl text-red-600': vendingDiscount > 0,
              'text-2xl text-primary': vendingDiscount === 0,
            })}
          >
            {formatCurrency(reservationPrices.totalPriceWithDiscount)}
          </span>
        </div>

        {availableCount > 1 && !isSold && (
          <div className="mt-4 flex items-center gap-3">
            <span className="text-sm font-medium text-muted-foreground">{translate('product.quantity')}</span>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                appearance="secondary"
                disabled={orderQuantity <= 1}
                onPress={() => setOrderQuantity((current) => Math.max(1, current - 1))}
              >
                -
              </Button>
              <span className="min-w-[1.5rem] text-center font-display-bold text-dark">{orderQuantity}</span>
              <Button
                size="sm"
                appearance="secondary"
                disabled={orderQuantity >= availableCount}
                onPress={() => setOrderQuantity((current) => Math.min(availableCount, current + 1))}
              >
                +
              </Button>
            </div>
          </div>
        )}

        <div className="mt-auto flex gap-2 pt-4">
          <NextLink href={`/${organisation.pathname}/${shopId}/product/${reservation._id}`} className="flex-1">
            <Button size="sm">{translate('product.infoButtonText')}</Button>
          </NextLink>
          <div className="flex-1">
            <Button
              size="sm"
              appearance="primary"
              disabled={isSold || availableCount === 0}
              onPress={() => {
                if (isSold || availableCount === 0) return;

                addToCart(availableReservations.slice(0, orderQuantity));
                router.push(`/${organisation.pathname}/${shopId}/order/shopping-cart`);
              }}
            >
              {translate(isSold ? 'product.sold' : 'product.orderButtonText')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface PageProps {
  organisation?: OrganisationObjectType;
  shop: ShopObjectType;
  shoppingCart: ShoppingCart;
  order?: OrderObjectType;
  reservations: Array<ReservationObjectType>;
  error?: MinimalServerError;
}

function PageContent(props: PageProps) {
  const { shop, organisation } = props;
  const { reservations } = useApplicationState();
  const { translate, language } = useTranslator();
  const { toggleKioskMode } = useKioskMode();
  const [searchValue, setSearchValue] = useState('');

  const handleSearchChange = (value: string) => {
    if (value === KIOSK_TOGGLE_SECRET) {
      toggleKioskMode();
      setSearchValue('');
      return;
    }

    setSearchValue(value);
  };

  const sortedReservationGroups = useMemo(() => {
    if (!reservations.length) {
      return [];
    }

    const getStatusPriority = (status: ReservationStatusEnumType) => {
      switch (status) {
        case ReservationStatusEnumType.Reserved:
          // Reserved should be at the very bottom
          return 2;
        case ReservationStatusEnumType.Ordered:
          // Ordered should be below other statuses
          return 1;
        default:
          // All other statuses come first
          return 0;
      }
    };

    const sortGroupsByStatus = (a: ReservationGroup, b: ReservationGroup) =>
      getStatusPriority(b.reservations[0].status) - getStatusPriority(a.reservations[0].status);

    const reservationGroups = groupReservations(reservations);

    if (!searchValue) {
      return [...reservationGroups].sort(sortGroupsByStatus);
    }

    const availableReservations = reservations.filter(isReservationAvailable);
    const availableGroups = groupReservations(availableReservations);

    const mappedValues = availableGroups.map((group) => {
      const productNames = group.reservations[0].vendingProducts
        .map((p) => (p.product ? getLocalisedString(p.product.name, language) : ''))
        .filter(Boolean);
      return {
        productNames,
      };
    });
    const fuse = new Fuse(mappedValues, {
      isCaseSensitive: false,
      keys: ['productNames'],
    });
    return fuse
      .search(searchValue)
      .map((v) => availableGroups[v.refIndex])
      .sort(sortGroupsByStatus);
  }, [reservations, searchValue, language]);

  return (
    <>
      <div className="bg-cream">
        <OrderHint />

        <div className="mx-auto max-w-7xl px-6 pt-8 pb-16">
          <Heading>{translate('products.currentlyAvailableTitle')}</Heading>
          <div className="relative mb-6 min-w-0" style={{ minWidth: '200px' }}>
            <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
              <SearchIcon className="h-4 w-4 text-muted-foreground" />
            </div>
            <label htmlFor="product-search" className="sr-only">
              {translate('products.search')}
            </label>
            <input
              id="product-search"
              type="search"
              value={searchValue}
              onChange={(event) => handleSearchChange(event.target.value)}
              placeholder={translate('products.searchPlaceholder')}
              className="w-full rounded-full border border-border bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-primary"
            />
          </div>

          {sortedReservationGroups.length > 0 ? (
            <div>
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {sortedReservationGroups.map((reservationGroup) => {
                  return (
                    <ProductCard
                      reservationGroup={reservationGroup}
                      organisation={nullthrows(organisation, 'Organisation not found')}
                      key={reservationGroup.reservations.map((reservation) => reservation._id).join('-')}
                    />
                  );
                })}
              </div>

              {!!shop.webshopUrl && (
                <div className="flex flex-col items-center my-8">
                  <div className="text-md mb-2">{translate('productsOverview.webshopFallbackTitle')}</div>
                  <LinkButton href={shop.webshopUrl}>{translate('productsOverview.webshopLinkButtonText')}</LinkButton>
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className="flex justify-center font-medium text-xl text-gray-700 my-32 text-center">
                {searchValue ? translate('products.noProductsFound') : translate('products.noProductsAvailable')}
              </div>
              {!!shop.webshopUrl && (
                <div className="flex flex-col items-center my-8">
                  <LinkButton href={shop.webshopUrl}>{translate('productsOverview.webshopLinkButtonText')}</LinkButton>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default function Index(props: PageProps) {
  let { organisation, shop, shoppingCart, order, reservations, error } = props;

  if (error) {
    return (
      <FallbackPageTemplate>
        <ErrorFallback error={error} />
      </FallbackPageTemplate>
    );
  }

  if (!organisation) {
    return (
      <FallbackPageTemplate>
        <NotFound />
      </FallbackPageTemplate>
    );
  }

  return (
    <ShopPageTemplate
      organisation={organisation}
      shop={shop}
      shoppingCart={shoppingCart}
      order={order}
      reservations={reservations}
    >
      <AppBanner url={shop._id ? `woyti://shop/${shop._id}` : 'woyti://'} />

      <PageContent {...props} />
    </ShopPageTemplate>
  );
}

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  try {
    let { shopId } = ctx.query;
    let organisationData = await getOrganisationAndShop(ctx, shopId as string);

    if (!organisationData) {
      return {
        props: {},
      };
    } else {
      try {
        return {
          props: {
            organisation: organisationData.organisation,
            shop: organisationData.shop,
            reservations: organisationData.reservations,
            shoppingCart: organisationData.shoppingCart,
            order: organisationData.order,
          },
        };
      } catch (err) {
        await captureException(err);
        return {
          props: {},
        };
      }
    }
  } catch (err) {
    const eventId = captureException(err);
    await Sentry.flush();

    return {
      props: {
        error: {
          message: err.message,
          eventId,
        },
      },
    };
  }
}
